// The case editor's footage panel: the shared video view over a body cam
// or surveillance upload, fed by the visual event log and the footage-check
// findings. Findings are also posted as suggestions; this panel shows them
// with the still and lets the officer attach stills to the export.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import cn from "classnames";
import { ArrowPathIcon, CheckIcon, ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import Dropdown from "../Settings/Dropdown";
import VideoView from "../common/video/VideoView";
import { VideoPoint, eventToPoint, formatClock, kindLabel } from "../common/video/types";
import { CaseFootageDetail, videoApi } from "../../services/video/api";
import { describeProgress } from "./FootageProgress";

interface Props {
  caseId: string;
  files: { id: string; file_name?: string | null; metadata?: any }[];
  onClose?: () => void;
  onOpenSuggestions?: () => void;
  // Playhead in ms and the file it belongs to, so the sidebar mini player
  // can resume from the same moment when the footage view closes.
  onTime?: (fileId: string, ms: number) => void;
  initialSeekMs?: number;
}

const STATUS_TEXT: Record<string, string> = {
  pending: "Waiting for the upload to finish",
  extracting: "Extracting stills from the footage",
  analyzing: "Reading the footage",
  completed: "Analyzed",
  failed: "Failed",
};

const FootagePanel: React.FC<Props> = ({ caseId, files, onClose, onOpenSuggestions, onTime, initialSeekMs }) => {
  const videoFiles = useMemo(() => files.filter((f) => f.metadata?.video), [files]);
  const [fileId, setFileId] = useState<string | null>(videoFiles[0]?.id || null);
  const [detail, setDetail] = useState<CaseFootageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [rerunning, setRerunning] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [stills, setStills] = useState<number[]>([]);

  useEffect(() => {
    if (!fileId && videoFiles[0]) setFileId(videoFiles[0].id);
  }, [videoFiles, fileId]);

  const load = useCallback(async () => {
    if (!fileId) return;
    try {
      const d = await videoApi.detail(caseId, fileId);
      setDetail(d);
      setStills(d.video.attachedStills || []);
    } catch {
      toast.error("Could not load the footage analysis");
    } finally {
      setLoading(false);
    }
  }, [caseId, fileId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Poll while the pipeline is working.
  useEffect(() => {
    const status = detail?.video.status;
    if (!status || status === "completed" || status === "failed") return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [detail?.video.status, load]);

  const points = useMemo<VideoPoint[]>(() => {
    if (!detail) return [];
    const events = detail.video.events.map(eventToPoint);
    const findings = (detail.video.findings || []).map<VideoPoint>((f) => ({
      id: `finding:${f.id}`,
      tStartMs: f.footage.t_start_ms,
      tEndMs: f.footage.t_end_ms,
      kind: f.kind,
      severity: f.severity,
      title: kindLabel(f.kind),
      description: f.footage.description,
      explanation: [f.narrative_quote ? `Report says: "${f.narrative_quote}"` : null, f.explanation].filter(Boolean).join(" "),
      frameTs: f.footage.frame_ts,
      source: "finding",
    }));
    return [...findings, ...events].sort((a, b) => a.tStartMs - b.tStartMs);
  }, [detail]);

  const rerun = async () => {
    if (!fileId) return;
    setRerunning(true);
    try {
      await videoApi.analyze(caseId, fileId);
      toast.success("Re-reading the footage");
      await load();
    } catch {
      toast.error("Could not start the analysis");
    } finally {
      setRerunning(false);
    }
  };

  const toggleStill = async (t: number) => {
    if (!fileId) return;
    const next = stills.includes(t) ? stills.filter((x) => x !== t) : [...stills, t].sort((a, b) => a - b);
    setStills(next);
    try {
      await videoApi.setStills(caseId, fileId, next);
    } catch {
      toast.error("Could not save the attachment");
    }
  };

  if (!videoFiles.length) return <div className="p-6 text-sm text-base-content/60">No footage analysis on this case.</div>;
  const video = detail?.video;
  const durationMs = Math.round((video?.durationSec || 0) * 1000) || detail?.transcript?.durationMs || 0;
  const frameUrl = (t: number) => videoApi.frameUrl(caseId, fileId!, t);
  const findings = video?.findings || [];

  const aside = (
    <div data-tour="footage-aside" className="border-b border-base-300 p-3 space-y-3 text-sm max-h-[45%] overflow-y-auto">
      {videoFiles.length > 1 && (
        <Dropdown
          size="xs"
          dense
          contentClassName="w-64"
          options={videoFiles.map((f) => ({ value: f.id, label: f.file_name || f.id }))}
          value={fileId || ""}
          onChange={(v) => {
            setFileId(v);
            setDetail(null);
          }}
        />
      )}
      {video && video.status !== "completed" && (() => {
        const d = describeProgress({ fileId: detail!.fileId, fileName: detail!.fileName, status: video.status, mode: video.mode, error: video.error, durationSec: video.durationSec, progress: video.progress || null, eventCount: video.eventCount ?? null });
        return (
          <div>
            <p className={cn("text-[13px]", video.status === "failed" ? "text-red-700 dark:text-red-400" : "text-base-content/60")}>
              {video.status !== "failed" && <span className="inline-block size-2 rounded-full bg-amber-500 animate-pulse mr-1.5" />}
              {d.label}
              {d.detail ? ` · ${d.detail}` : ""}
              {d.pct != null && video.status !== "failed" ? ` · ${d.pct}%` : ""}
            </p>
            {d.pct != null && video.status !== "failed" && (
              <div className="mt-1.5 h-1.5 rounded-full bg-base-300 overflow-hidden">
                <div className="h-full rounded-full bg-blue-500 transition-[width] duration-700 ease-out" style={{ width: `${Math.max(2, d.pct)}%` }} />
              </div>
            )}
          </div>
        );
      })()}
      {video?.summary && (
        <div>
          <button type="button" className="w-full flex items-center gap-1 font-semibold text-base-content" onClick={() => setSummaryOpen((o) => !o)}>
            What the footage shows
            <ChevronDownIcon className={cn("size-3.5 text-base-content/50 transition-transform ml-auto", summaryOpen && "rotate-180")} />
          </button>
          <div className={cn("grid transition-[grid-template-rows] duration-300 ease-in-out", summaryOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}>
            <div className="min-h-0 overflow-hidden">
              <p className="mt-1.5 text-[13px] leading-5 text-base-content/80 whitespace-pre-wrap">{video.summary}</p>
            </div>
          </div>
        </div>
      )}
      {video?.status === "completed" && (
        <div data-tour="footage-check" className="rounded-lg border border-base-300 bg-base-100 p-2">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold text-base-content/80">Footage check</p>
            <span className="text-[11px] text-base-content/50">
              {video.mode === "enrich" ? "Footage was written into the draft" : video.checkedAt ? `${findings.length} finding${findings.length === 1 ? "" : "s"}` : "Runs when the draft is ready"}
            </span>
            {findings.length > 0 && onOpenSuggestions && (
              <button type="button" className="ml-auto text-xs text-blue-600 dark:text-blue-300 hover:underline" onClick={onOpenSuggestions}>
                Review in Suggestions
              </button>
            )}
          </div>
          <p className="mt-1 text-[11px] text-base-content/50">
            Findings compare the draft with what the camera shows. They are suggestions; the report stays your account.
          </p>
        </div>
      )}
      {stills.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-base-content/80 mb-1">Stills attached to the export ({stills.length})</p>
          <div className="flex flex-wrap gap-1">
            {stills.map((t) => (
              <button key={t} type="button" className="relative group" title={`Remove ${formatClock(t * 1000)}`} onClick={() => toggleStill(t)}>
                <img src={frameUrl(t)} alt="" className="w-16 h-9 object-cover rounded bg-base-300" />
                <span className="absolute bottom-0 left-0 right-0 text-[9px] text-white bg-black/60 text-center tabular-nums">{formatClock(t * 1000)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="xs" disabled={rerunning || !video || video.status === "extracting" || video.status === "pending"} onClick={rerun}>
          <ArrowPathIcon className={cn("size-3.5 mr-1", rerunning && "animate-spin")} /> Re-read footage
        </Button>
        {onClose && (
          <Button variant="clean" size="xs" className="ml-auto" onClick={onClose}>
            Edit transcription
          </Button>
        )}
      </div>
    </div>
  );

  if (loading && !detail) {
    return (
      <div className="p-6 animate-pulse space-y-4">
        <div className="aspect-video bg-base-300 rounded-xl max-w-2xl" />
        <div className="h-24 bg-base-300 rounded" />
      </div>
    );
  }
  if (!detail) return <div className="p-6 text-sm text-base-content/60">Footage analysis is not available for this file.</div>;

  return (
    <div className="h-full min-h-0 flex flex-col">
      <VideoView
        src={detail.url}
        posterUrl={video?.frames?.length ? frameUrl(video.frames[Math.min(5, video.frames.length - 1)]) : undefined}
        durationMs={durationMs}
        points={points}
        idle={video?.idle}
        transcript={detail.transcript}
        participants={detail.participants}
        frameUrl={frameUrl}
        transcriptScrollKey={`case.video.${detail.fileId}`}
        listTitle="What happened"
        listEmptyText={video?.status === "completed" ? "Nothing of note in this footage." : "Reading the footage…"}
        storageKey="pnai-footageRailWidth"
        aside={aside}
        initialSeekMs={initialSeekMs}
        onTime={(ms) => onTime?.(detail.fileId, ms)}
      />
      {/* Attach the still at the current point from the list: a small strip of the flagged stills. */}
      {points.length > 0 && (
        <div data-tour="footage-attach" className="border-t border-base-300 px-3 py-2 flex items-center gap-2 overflow-x-auto">
          <span className="text-[11px] text-base-content/50 shrink-0">Attach a still to the report:</span>
          {[...new Set(points.flatMap((p) => p.frameTs.slice(0, 1)))].slice(0, 40).map((t) => {
            const on = stills.includes(t);
            return (
              <button key={t} type="button" data-tour="footage-still" onClick={() => toggleStill(t)} className={cn("relative shrink-0 rounded overflow-hidden border-2", on ? "border-blue-500" : "border-transparent hover:border-base-300")} title={on ? "Attached" : `Attach ${formatClock(t * 1000)}`}>
                <img src={frameUrl(t)} alt="" className="w-20 h-[45px] object-cover bg-base-300" loading="lazy" />
                <span className="absolute bottom-0 left-0 right-0 text-[9px] text-white bg-black/60 text-center tabular-nums">{formatClock(t * 1000)}</span>
                <span className={cn("absolute top-0.5 right-0.5 rounded-full p-0.5", on ? "bg-blue-500 text-white" : "bg-black/50 text-white")}>{on ? <CheckIcon className="size-2.5" /> : <PlusIcon className="size-2.5" />}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FootagePanel;
